import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CircleAlert, Star } from "lucide-react";
import OnboardingSectionCard from "../OnboardingSectionCard";
import CompanyAssetsSection from "./CompanyAssetsSection";
import SignalTriggerConfig from "@/shared/components/ui/SignalTriggerConfig";
import { definePitchSchema } from "../../../../schema/OnBoarding";
import { getZodErrorMessages } from "@/features/campaigns/components/UserCampaigns/components/wizardValidationUtils";
import { addBulletPoints } from "@/shared/utils/helperFunctions";
import { useMainStore } from "@/stores";
import useUnsavedChangesWarning from "@/shared/hooks/useUnsavedChangesWarning";

const parseContext = (contextStr) => {
  if (!contextStr) {
    return { type: "case_study", clientName: "", description: "", link: "" };
  }
  if (typeof contextStr === "object") {
    return {
      type: contextStr.type || "case_study",
      clientName: contextStr.clientName || "",
      description: contextStr.description || "",
      link: contextStr.link || "",
    };
  }
  let str = contextStr;
  try {
    const parsedOnce = JSON.parse(str);
    if (typeof parsedOnce === "string") {
      str = parsedOnce;
    }
  } catch (e) {}

  try {
    const parsed = JSON.parse(str);
    if (parsed && typeof parsed === "object") {
      return {
        type: parsed.type || "case_study",
        clientName: parsed.clientName || "",
        description: parsed.description || "",
        link: parsed.link || "",
      };
    }
  } catch (e) {}

  return {
    type: "case_study",
    clientName: "",
    description: str || "",
    link: "",
  };
};

const stringifyContext = (structured) => {
  return JSON.stringify({
    type: structured.type,
    clientName: structured.clientName,
    description: structured.description,
    link: structured.link,
  });
};

const hasRowDetails = (row) =>
  [row.clientName, row.description, row.link].some(
    (value) => typeof value === "string" && value.trim(),
  );

export default function DefinePitch({
  registerSubmitHandler,
  onDataChange,
  initialCompanyAssets = [],
  initialValues = null,
  _isEditMode = false,
  suppressUnsavedWarning = false,
}) {
  const { companyProfile, setCompanyProfile } = useMainStore();

  const [activeType, setActiveType] = useState("case_study");
  const [localAssets, setLocalAssets] = useState([]);
  const [pendingDeletes, setPendingDeletes] = useState(new Set());
  const pendingDeletesRef = useRef(new Set());

  // Build localAssets from initialCompanyAssets
  useEffect(() => {
    let finalAssets = [];
    if (!initialCompanyAssets || !Array.isArray(initialCompanyAssets)) {
      finalAssets = [];
    } else {
      const isObjectAssets = initialCompanyAssets.some(
        (item) => item && typeof item === "object",
      );

      if (isObjectAssets) {
        finalAssets = initialCompanyAssets
          .filter((asset) => asset && typeof asset === "object")
          .map((asset, idx) => {
            const parsed = parseContext(
              asset.context ||
                initialValues?.companyAssetsDetails?.[idx] ||
                "",
            );
            return {
              ...(asset.id ? { id: asset.id } : {}),
              ...(asset.fileId
                ? { fileId: asset.fileId }
                : !asset.id
                  ? { fileId: `local-persisted-${Date.now()}-${idx}` }
                  : {}),
              name: asset.file_name || asset.name || "",
              file_name: asset.file_name || asset.name,
              size: asset.file_size || 0,
              mime_type: asset.mime_type || "",
              file: null,
              type: parsed.type,
              clientName: parsed.clientName,
              description: parsed.description,
              link: parsed.link,
            };
          });
      } else {
        const fileNames = initialCompanyAssets.filter(
          (item) => typeof item === "string",
        );
        finalAssets = fileNames.map((name, idx) => {
          const parsed = parseContext("");
          return {
            fileId: `local-${Date.now()}-${idx}`,
            name,
            file: null,
            type: parsed.type,
            clientName: parsed.clientName,
            description: parsed.description,
            link: parsed.link,
          };
        });
      }
    }

    const persistedDetails = Array.isArray(initialValues?.companyAssetsDetails)
      ? initialValues.companyAssetsDetails
      : [];
    const hasUnattachedRows = finalAssets.some(
      (asset) => !asset.id && hasRowDetails(asset),
    );
    if (!hasUnattachedRows && persistedDetails.length > finalAssets.length) {
      persistedDetails.slice(finalAssets.length).forEach((context, idx) => {
        const parsed = parseContext(context);
        if (!hasRowDetails(parsed)) return;
        finalAssets.push({
          fileId: `local-persisted-${Date.now()}-${idx}`,
          type: parsed.type,
          clientName: parsed.clientName,
          description: parsed.description,
          file: null,
          name: "",
          link: parsed.link,
        });
      });
    }

    // Ensure at least one empty row exists for each type by default
    const types = ["case_study", "thought_leadership", "marketing_collateral"];
    types.forEach((type) => {
      const hasType = finalAssets.some((a) => a.type === type);
      if (!hasType) {
        finalAssets.push({
          fileId: `local-init-${type}-${Date.now()}`,
          type,
          clientName: "",
          description: "",
          file: null,
          name: "",
          link: "",
        });
      }
    });

    setLocalAssets(finalAssets);
    const empty = new Set();
    setPendingDeletes(empty);
    pendingDeletesRef.current = empty;
  }, [initialCompanyAssets, initialValues?.companyAssetsDetails]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    getValues,
    formState: { errors, isDirty },
  } = useForm({
    mode: "all",
    resolver: zodResolver(definePitchSchema),
    defaultValues: {
      pain_points_solved: initialValues?.painPointsSolved
        ? addBulletPoints(initialValues.painPointsSolved)
        : "",
      value_proposition: initialValues?.valueProposition || "",
      company_level_trigger:
        initialValues?.companyLevelSalesTrigger ||
        initialValues?.salesTrigger ||
        [],
      industry_level_trigger: initialValues?.industryLevelTrigger || [],
      signal_trigger_config:
        initialValues?.signalTriggerConfig ||
        initialValues?.signal_trigger_config ||
        null,
    },
  });

  const formValues = watch([
    "value_proposition",
    "company_level_trigger",
    "pain_points_solved",
    "industry_level_trigger",
    "signal_trigger_config",
  ]);
  const [
    valueProposition,
    companyLevelSalesTrigger,
    painPointsSolved,
    industryLevelTrigger,
    signalTriggerConfig,
  ] = formValues;
  const formValuesRef = useRef({
    valueProposition,
    companyLevelSalesTrigger,
    painPointsSolved,
    industryLevelTrigger,
    signalTriggerConfig,
  });
  const dataChangeTimeoutRef = useRef(null);

  useEffect(() => {
    formValuesRef.current = {
      valueProposition,
      companyLevelSalesTrigger,
      painPointsSolved,
      industryLevelTrigger,
      signalTriggerConfig,
    };

    if (dataChangeTimeoutRef.current) {
      clearTimeout(dataChangeTimeoutRef.current);
    }
    dataChangeTimeoutRef.current = setTimeout(() => {
      if (onDataChange) {
        onDataChange({
          valueProposition,
          companyLevelSalesTrigger,
          painPointsSolved,
          industryLevelTrigger,
          signalTriggerConfig,
        });
      }
    }, 300);
    return () => {
      if (dataChangeTimeoutRef.current) {
        clearTimeout(dataChangeTimeoutRef.current);
      }
    };
  }, [
    painPointsSolved,
    valueProposition,
    companyLevelSalesTrigger,
    industryLevelTrigger,
    signalTriggerConfig,
    onDataChange,
  ]);

  // Only notify parent when user makes changes (handlers), not on initial load,

  useEffect(() => {
    if (registerSubmitHandler) {
      const submitHandler = async () => {
        const isValid = await trigger();
        if (!isValid) {
          return {
            success: false,
            fieldErrors: getZodErrorMessages(definePitchSchema, getValues()),
          };
        }

        const {
          valueProposition,
          companyLevelSalesTrigger,
          painPointsSolved,
          industryLevelTrigger,
          signalTriggerConfig,
        } = formValuesRef.current;

        setCompanyProfile({
          ...companyProfile,
          pain_points_solved: painPointsSolved,
          value_proposition: valueProposition,
          company_level_trigger: companyLevelSalesTrigger,
          industry_level_trigger: industryLevelTrigger,
          signal_trigger_config: signalTriggerConfig,
        });

        const keptExisting = localAssets.filter(
          (a) => a.id && !pendingDeletes.has(a.id),
        );
        const newFiles = localAssets.filter((a) => a.file && !a.id);
        const newAssetDetails = localAssets
          .filter((a) => !a.id && !a.file && hasRowDetails(a))
          .map((a) =>
            stringifyContext({
              type: a.type,
              clientName: a.clientName,
              description: a.description,
              link: a.link,
            }),
          );

        console.log("[DefinePitch] asset payload", {
          totalRows: localAssets.length,
          existingRows: keptExisting.length,
          newFileRows: newFiles.length,
          newDetailRows: newAssetDetails.length,
        });

        return {
          success: true,
          data: {
            painPointsSolved,
            valueProposition,
            companyLevelSalesTrigger,
            industryLevelTrigger,
            signalTriggerConfig,
            companyAssetIds: keptExisting.map((a) => a.id),
            companyAssetsDetails: keptExisting.map((a) =>
              stringifyContext({
                type: a.type,
                clientName: a.clientName,
                description: a.description,
                link: a.link,
              }),
            ),
            pendingDeletes: Array.from(pendingDeletes),
            newFiles: newFiles.map((a) => ({
              file: a.file,
              context: stringifyContext({
                type: a.type,
                clientName: a.clientName,
                description: a.description,
                link: a.link,
              }),
            })),
            newAssetDetails,
          },
        };
      };
      registerSubmitHandler(submitHandler);
    }
  }, [
    registerSubmitHandler,
    trigger,
    getValues,
    setCompanyProfile,
    companyProfile,
    localAssets,
    pendingDeletes,
  ]);

  const hasPendingAssetChanges = useMemo(
    () =>
      pendingDeletes.size > 0 ||
      localAssets.some(
        (a) => (!a.id && Boolean(a.file)) || (!a.id && hasRowDetails(a)),
      ),
    [pendingDeletes, localAssets],
  );

  useUnsavedChangesWarning(
    suppressUnsavedWarning ? false : isDirty || hasPendingAssetChanges,
  );

  const handlePainPointChange = useCallback(
    (e) => {
      const inputText = e.target.value;
      const bulletedText = addBulletPoints(inputText);
      setValue("pain_points_solved", bulletedText, { shouldValidate: true });
    },
    [setValue],
  );

  const handleUpdateRowField = (fileIdOrId, field, value) => {
    setLocalAssets((prev) =>
      prev.map((a) => {
        const isMatch = a.id
          ? String(a.id) === String(fileIdOrId)
          : String(a.fileId) === String(fileIdOrId);
        if (isMatch) {
          return { ...a, [field]: value };
        }
        return a;
      }),
    );
  };

  const handleAddRow = () => {
    const newRow = {
      fileId: `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: activeType,
      clientName: "",
      description: "",
      file: null,
      name: "",
      link: "",
    };
    setLocalAssets((prev) => [...prev, newRow]);
  };

  const handleDeleteRow = (fileIdOrId) => {
    setLocalAssets((prev) => {
      const filtered = prev.filter((a) =>
        a.id ? String(a.id) !== String(fileIdOrId) : String(a.fileId) !== String(fileIdOrId)
      );

      // Track delete if existing row
      const asset = prev.find((a) =>
        a.id ? String(a.id) === String(fileIdOrId) : String(a.fileId) === String(fileIdOrId)
      );
      if (asset?.id) {
        setPendingDeletes((p) => {
          const next = new Set([...p, asset.id]);
          pendingDeletesRef.current = next;
          return next;
        });
      }

      // If deleting this row makes the activeType empty, keep one empty row
      const activeTypeRows = filtered.filter((a) => a.type === activeType);
      if (activeTypeRows.length === 0) {
        return [
          ...filtered,
          {
            fileId: `local-init-${activeType}-${Date.now()}`,
            type: activeType,
            clientName: "",
            description: "",
            file: null,
            name: "",
            link: "",
          },
        ];
      }
      return filtered;
    });
  };

  return (
    <div className="w-full flex flex-col space-y-8 scrollbar-hide">
      <form
        id="define-pitch-form"
        onSubmit={handleSubmit(() => {
          const {
            painPointsSolved,
            valueProposition,
            companyLevelSalesTrigger,
            industryLevelTrigger,
            signalTriggerConfig,
          } = formValuesRef.current;
          setCompanyProfile({
            ...companyProfile,
            pain_points: painPointsSolved,
            value_proposition: valueProposition,
            company_level_trigger: companyLevelSalesTrigger,
            industry_level_trigger: industryLevelTrigger,
            signal_trigger_config: signalTriggerConfig,
          });
        })}
        className="w-full flex flex-col space-y-8"
      >
        <OnboardingSectionCard
          icon={<CircleAlert className="text-gray-500" size={20} />}
          title="Pain Points"
        >
          <textarea
            {...register("pain_points_solved")}
            className={`bg-transparent w-full p-6 border rounded-lg text-gray-800 dark:text-gray-100 text-sm resize-none h-40 
    focus:outline-none leading-6 ${
      errors.pain_points_solved
        ? "border-red-500"
        : "border-gray-300 dark:border-gray-800 focus:border-secondary focus:ring-1 focus:ring-secondary"
    }`}
            value={painPointsSolved || ""}
            onChange={handlePainPointChange}
          ></textarea>
          {errors.pain_points_solved && (
            <p className="text-red-500 text-xs mt-1">
              {errors.pain_points_solved.message}
            </p>
          )}
        </OnboardingSectionCard>

        <OnboardingSectionCard
          icon={<Star className="text-gray-500" size={20} />}
          title="Value Proposition"
        >
          <textarea
            {...register("value_proposition")}
            className={`bg-transparent w-full p-6 border rounded-lg text-gray-800 dark:text-gray-100 text-sm resize-none 
            focus:outline-none ${
              errors.value_proposition
                ? "border-red-500"
                : "border-gray-300 dark:border-gray-800 focus:border-secondary focus:ring-1 focus:ring-secondary"
            }`}
            value={valueProposition}
            onChange={(e) => {
              setValue("value_proposition", e.target.value, {
                shouldValidate: true,
              });
              e.target.style.height = "auto";
              e.target.style.height = `${e.target.scrollHeight}px`;
            }}
            style={{ height: "auto" }}
          ></textarea>
          {errors.value_proposition && (
            <p className="text-red-500 text-xs mt-1">
              {errors.value_proposition.message}
            </p>
          )}
        </OnboardingSectionCard>

        <div className="space-y-2">
          <SignalTriggerConfig
            initialCategories={
              signalTriggerConfig ??
              (initialValues?.signalTriggerConfig || initialValues?.signal_trigger_config)
            }
            companyLevelTriggers={
              initialValues?.companyLevelSalesTrigger ||
              initialValues?.salesTrigger ||
              companyLevelSalesTrigger ||
              []
            }
            industryLevelTriggers={
              initialValues?.industryLevelTrigger ||
              industryLevelTrigger ||
              []
            }
            onChange={({
              companyLevelSalesTrigger: compTrigs,
              industryLevelTrigger: indTrigs,
              signalTriggerConfig: cfg,
            }) => {
              setValue("company_level_trigger", compTrigs, {
                shouldValidate: true,
                shouldDirty: true,
              });
              setValue("industry_level_trigger", indTrigs, {
                shouldValidate: true,
                shouldDirty: true,
              });
              setValue("signal_trigger_config", cfg, {
                shouldDirty: true,
              });
            }}
          />
          {(errors.company_level_trigger || errors.industry_level_trigger) && (
            <div className="space-y-1 px-1">
              {errors.company_level_trigger && (
                <p className="text-red-500 text-xs">
                  {errors.company_level_trigger.message}
                </p>
              )}
              {errors.industry_level_trigger && (
                <p className="text-red-500 text-xs">
                  {errors.industry_level_trigger.message}
                </p>
              )}
            </div>
          )}
        </div>
      </form>

      {/* Company Assets outside form to prevent Add button from triggering form submit */}
      <CompanyAssetsSection
        localAssets={localAssets}
        activeType={activeType}
        setActiveType={setActiveType}
        handleAddRow={handleAddRow}
        handleDeleteRow={handleDeleteRow}
        handleUpdateRowField={handleUpdateRowField}
      />
    </div>
  );
}
